// ── Historial de fichajes DÍA A DÍA (ficha del trabajador, web) ───────────
// Construye UNA FILA POR DÍA para el rango del historial:
//   · días con algún evento (de cualquier tipo; el filtro de tipo de la
//     página decide después qué filas/eventos se ven), y
//   · días laborables SIN NINGÚN FICHAJE ("Sin fichar"): jornada > 0 según el
//     horario vigente en esa fecha, no festivo, sin vacaciones / día completo /
//     compensación aprobados, anteriores a HOY (igual que el corte de la bolsa
//     en "ayer") y desde que el trabajador empezó a fichar (y desde el epoch
//     de la bolsa y su fecha de inicio de cómputo).
//
// FECHA DE INICIO DE CÓMPUTO (`computationStart`, el "alta" del trabajador):
// los días anteriores NO computan. Decisión para esta tabla: la fila SIGUE
// apareciendo si ese día hay fichajes (no se oculta nada de lo que el
// trabajador hizo), pero se marca como tipo de día "Fuera de cómputo", sin
// jornada esperada ni diferencia (ambas a null) — así no suma al resumen ni a
// la conciliación con la bolsa. Los días laborables SIN fichar anteriores al
// alta no se inventan: no aparecen como "Sin fichar".
//
// CONSISTENCIA CON LA BOLSA: la jornada esperada sale de
// makeBalanceDayResolver y el periodo de balanceComputationRange
// (src/lib/hourBalance.js), las MISMAS funciones que usa computeAutoBalance, y
// el trabajado de computeDayWork (idéntico a la bolsa). Dentro del periodo de
// la bolsa, la suma de `balanceMinutes` de las filas === total automático de
// la bolsa (sin ajustes manuales). Ver reconcileWithBalance y su test.
//
// Módulo PURO (sin React): testeado en src/utils/__tests__/workerHistoryDays.test.js.

import { EVENT_TYPES, APPROVAL_STATUS } from '../services/api';
import {
  computeDayWork, makeBalanceDayResolver, balanceComputationRange, hasAnySchedule,
  isRejectedWorkTimeEvent, BALANCE_EPOCH_ISO,
} from '../lib/hourBalance';
import {
  DEFAULT_TIME_ZONE, dayKeyInZone, dayKeysBetween, addDaysToDayKey, todayInZone, dowOfDayKey,
} from '../lib/workerTime';

const WORK_TYPES = new Set([
  EVENT_TYPES.CLOCK_IN, EVENT_TYPES.CLOCK_OUT, EVENT_TYPES.PAUSE_START, EVENT_TYPES.PAUSE_END,
]);
const isWorkTimeEvent = (e) => !!e && WORK_TYPES.has(Number(e.eventType));
const isClockEvent = (e) => !!e && (Number(e.eventType) === EVENT_TYPES.CLOCK_IN || Number(e.eventType) === EVENT_TYPES.CLOCK_OUT);
const isPendingApproval = (e) => !!e && e.approvalStatus != null && e.approvalStatus !== ''
  && Number(e.approvalStatus) === APPROVAL_STATUS.PENDING;

// ── Tipos de día ──────────────────────────────────────────────────────────
export const DAY_TYPE_LABELS = {
  laborable: 'Laborable',
  descanso: 'Descanso',
  festivo: 'Festivo',
  vacaciones: 'Vacaciones',
  baja: 'Día completo',
  ausencia: 'Ausencia por horas',
  compensacion: 'Compensación',
  compensacionParcial: 'Horas compensadas',
  fueraComputo: 'Fuera de cómputo',
};

export const WEEKDAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// ── Avisos de computeDayWork agrupados ────────────────────────────────────
// Sin abrir: algo se cierra sin haberse abierto.
const SIN_ABRIR_WARNINGS = new Set(['orphanClockOut', 'orphanPauseEnd', 'pauseStartWhileOut']);
// Sin cerrar (abiertos al final del día) → flags openClockIn / openPause.
const SIN_CERRAR_WARNINGS = new Set(['openPause']);

export const OTHER_WARNING_LABELS = {
  duplicateClockIn: 'Entrada duplicada',
  clockInWhilePaused: 'Entrada durante una pausa',
  duplicatePauseStart: 'Pausa duplicada',
  pauseClosedByClockOut: 'Pausa cerrada por la salida',
};

// ── Flags (badges) ────────────────────────────────────────────────────────
export const FLAG_INFO = {
  sinCerrar: { label: 'Sin cerrar', tooltip: 'Entrada sin salida o pausa sin cerrar al final del día: el tiempo abierto no cuenta en la bolsa.' },
  sinAbrir: { label: 'Sin abrir', tooltip: 'Salida sin entrada, fin de pausa sin inicio o pausa iniciada fuera de jornada: ese fichaje no cuenta.' },
  sinFichar: { label: 'Sin fichar', tooltip: 'Día laborable sin ningún fichaje: la bolsa lo descuenta como falta (−jornada).' },
  porAprobar: { label: 'Por aprobar', tooltip: 'Tiene fichajes pendientes de aprobación.' },
  otras: { label: 'Incidencia', tooltip: 'Fichajes que no encajan (entrada o pausa duplicada, pausa cerrada por la salida…).' },
};

// ── Filtros de estado (chips, selección única) ────────────────────────────
//   · pendientes  = días que requieren que alguien actúe: sin cerrar, sin
//                   abrir, sin fichar o con fichajes por aprobar.
//   · incidencias = pendientes + cualquier otro aviso de computeDayWork
//                   (entrada/pausa duplicada, pausa cerrada por la salida…).
export const HISTORY_STATUS_FILTERS = [
  { key: 'todos', label: 'Todos', tooltip: 'Todos los días del rango', test: () => true },
  { key: 'pendientes', label: 'Pendientes', tooltip: 'Días que requieren revisión: sin cerrar, sin abrir, sin fichar o con fichajes por aprobar', test: (r) => r.pendiente },
  { key: 'sinCerrar', label: 'Sin cerrar', tooltip: FLAG_INFO.sinCerrar.tooltip, test: (r) => r.flags.sinCerrar },
  { key: 'sinAbrir', label: 'Sin abrir', tooltip: FLAG_INFO.sinAbrir.tooltip, test: (r) => r.flags.sinAbrir },
  { key: 'sinFichar', label: 'Sin fichar', tooltip: FLAG_INFO.sinFichar.tooltip, test: (r) => r.flags.sinFichar },
  { key: 'porAprobar', label: 'Por aprobar', tooltip: FLAG_INFO.porAprobar.tooltip, test: (r) => r.flags.porAprobar },
  { key: 'incidencias', label: 'Con incidencias', tooltip: 'Pendientes + cualquier otro fichaje que no encaja (entradas o pausas duplicadas, pausa cerrada por la salida…)', test: (r) => r.conIncidencias },
  { key: 'exceso', label: 'Se pasa', tooltip: 'Días en los que trabajó por encima de su jornada más que la desviación elegida', test: (r, o) => classifyDeviation(r, o && o.threshold) === 'exceso' },
  { key: 'defecto', label: 'No llega', tooltip: 'Días en los que trabajó por debajo de su jornada más que la desviación elegida (incluye los días sin fichar)', test: (r, o) => classifyDeviation(r, o && o.threshold) === 'defecto' },
];

// ── Desviación respecto a la jornada (se pasa / no llega) ─────────────────
// Umbrales ofrecidos en la página (minutos) y el de por defecto (1 h).
export const DEVIATION_THRESHOLDS = [30, 60, 120];
export const DEFAULT_DEVIATION_THRESHOLD = 60;
const normThreshold = (t) => {
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_DEVIATION_THRESHOLD;
};
// classifyDeviation(row, thresholdMin) → 'exceso' | 'defecto' | null
//   · Solo días que la bolsa cuenta como LABORABLES: balance numérico
//     (pasado, con horario) y jornada esperada > 0. Hoy/futuro, sin horario,
//     festivos, descansos, vacaciones… → null.
//   · exceso  si balance >= +umbral; defecto si balance <= −umbral (límite
//     INCLUSIVO).
//   · "Sin fichar" es SIEMPRE defecto (falta la jornada entera, aunque la
//     jornada sea menor que el umbral).
//   · Día con HORAS COMPENSADAS (compensación por tramos aprobada) → null.
//     El déficit está justificado: el trabajador salió antes y esas horas
//     salen de su bolsa. No es un día que haya que perseguir, así que no
//     aparece en "No llega" (ni en "Se pasa").
export const classifyDeviation = (row, threshold = DEFAULT_DEVIATION_THRESHOLD) => {
  if (!row) return null;
  const bal = row.balanceMinutes;
  if (bal == null || !Number.isFinite(Number(bal))) return null;
  if (!(Number(row.expectedMinutes) > 0) || row.isToday || row.isFuture) return null;
  if (row.partialCompensation) return null;
  if (row.flags && row.flags.sinFichar) return 'defecto';
  const t = normThreshold(threshold);
  if (bal >= t) return 'exceso';
  if (bal <= -t) return 'defecto';
  return null;
};

// rowHighlight(row, thresholdMin) → 'abierto' | 'defecto' | 'exceso' | null
// Color de fondo de la fila (uno solo). Precedencia:
//   abierto (sin cerrar o sin abrir: fichajes mal cerrados/abiertos que hay
//   que corregir; comparten el ámbar) > defecto (incl. sin fichar) > exceso.
// Las demás condiciones se siguen mostrando como badges.
export const rowHighlight = (row, threshold = DEFAULT_DEVIATION_THRESHOLD) => {
  if (!row) return null;
  if (row.flags && (row.flags.sinCerrar || row.flags.sinAbrir)) return 'abierto';
  return classifyDeviation(row, threshold);
};

const eventKey = (e) => (e.id != null ? `id:${e.id}` : `t:${e.eventType}|${e.timestamp}`);
const unionEvents = (a, b) => {
  const seen = new Set();
  const out = [];
  for (const e of [...(a || []), ...(b || [])]) {
    if (!e) continue;
    const k = eventKey(e);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(e);
  }
  return out;
};

const dayTypeFor = (info) => {
  if (!info) return null;
  // Anterior al alta del trabajador: manda sobre cualquier otro tipo.
  if (info.beforeStart) return 'fueraComputo';
  if (info.isHoliday) return 'festivo';
  if (info.onVacation) return 'vacaciones';
  if (info.onFullDay) return 'baja';
  if (info.onCompensation) return 'compensacion';
  if (info.slotMinutes === 0) return 'descanso';
  // Compensación POR TRAMOS: el día se trabaja, pero esas horas están
  // justificadas (se pagan con la bolsa). Va antes que 'ausencia' por la
  // misma razón que el resto de justificaciones: es lo que explica el día.
  if (info.partialCompensation) return 'compensacionParcial';
  if (info.hourlyAbsenceMinutes > 0) return 'ausencia';
  return 'laborable';
};

// buildWorkerHistoryDays({
//   rangeEvents,    ← eventos cargados para el historial (rango visible)
//   balanceEvents,  ← los MISMOS eventos que usa la bolsa (balanceEventsRange)
//   scheduleInput,  ← mismo input de horario que la bolsa (balanceScheduleInput)
//   holidaysSet,    ← Set<'YYYY-MM-DD'> de festivos del trabajador
//   timeZone,       ← zona del trabajador
//   startKey, endKey ← rango del historial ('YYYY-MM-DD', inclusivo)
//   computationStart ← fecha de inicio de cómputo del trabajador o null
//                      (computationStartKey de sus offsets de /api/hourBalance)
//   now,            ← inyectable (tests)
// }) → { rows, noSchedule, balancePeriod: { startKey, endKey } | null, todayKey }
// rows ordenadas por fecha DESC. Cada fila:
//   { date, weekday, weekdayLabel, events, firstIn, lastOut, clockIns, clockOuts,
//     workedMinutes, pauseMinutes, expectedMinutes, balanceMinutes,
//     hourlyAbsenceMinutes, partialCompensation, compensationMinutes, dayType,
//     isToday, isFuture, inBalancePeriod, work,
//     flags: { sinCerrar, sinAbrir, sinFichar, porAprobar },
//     otherWarnings: [type], warningTypes: [type], pendiente, conIncidencias }
// expectedMinutes/balanceMinutes: null sin horario; balanceMinutes también
// null en hoy/futuro (el día no ha terminado; la bolsa tampoco lo cuenta).
export const buildWorkerHistoryDays = ({
  rangeEvents = [],
  balanceEvents = [],
  scheduleInput = null,
  holidaysSet = null,
  timeZone = DEFAULT_TIME_ZONE,
  startKey,
  endKey,
  computationStart = null,
  now = Date.now(),
} = {}) => {
  const tz = timeZone || DEFAULT_TIME_ZONE;
  const todayKey = todayInZone(tz, now);
  const yesterdayKey = addDaysToDayKey(todayKey, -1);
  const noSchedule = !hasAnySchedule(scheduleInput);

  // Periodo de la bolsa y resolvers de jornada esperada.
  //   · Dentro del periodo: resolver construido EXACTAMENTE con los eventos
  //     de la bolsa (mismos periodos de vacaciones/ausencias → misma cifra).
  //   · Fuera: con la unión de ambos (el rango visible puede ir más atrás).
  const balancePeriod = noSchedule ? null : balanceComputationRange(balanceEvents, tz, now, computationStart);
  const inPeriod = (k) => !!balancePeriod && k >= balancePeriod.startKey && k <= balancePeriod.endKey;
  const allEvents = unionEvents(balanceEvents, rangeEvents);
  const balanceResolver = noSchedule ? null : makeBalanceDayResolver(balanceEvents, scheduleInput, holidaysSet, tz, computationStart);
  const unionResolver = noSchedule ? null : makeBalanceDayResolver(allEvents, scheduleInput, holidaysSet, tz, computationStart);
  const infoFor = (k) => {
    if (noSchedule) return null;
    return inPeriod(k) ? balanceResolver(k) : unionResolver(k);
  };

  // Eventos del rango agrupados por día (zona del trabajador).
  const byDay = new Map();
  for (const e of Array.isArray(rangeEvents) ? rangeEvents : []) {
    if (!e) continue;
    const k = dayKeyInZone(e.timestamp, tz);
    if (!k || (startKey && k < startKey) || (endKey && k > endKey)) continue;
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k).push(e);
  }

  // Candidatos "Sin fichar": desde el primer fichaje real del trabajador (y
  // el epoch de la bolsa) hasta AYER, dentro del rango visible.
  const firstClockTs = allEvents
    .filter((e) => isClockEvent(e) && !isRejectedWorkTimeEvent(e))
    .map((e) => new Date(e.timestamp).getTime())
    .filter(Number.isFinite);
  const dayKeys = new Set(byDay.keys());
  if (!noSchedule && firstClockTs.length && startKey && endKey) {
    const activityKey = dayKeyInZone(Math.min(...firstClockTs), tz);
    // El alta del trabajador entra en el max: antes de ella no se inventan
    // días "Sin fichar" (el resolver ya daría expected 0, pero así ni se
    // recorren).
    const from = [startKey, activityKey, BALANCE_EPOCH_ISO, computationStart || BALANCE_EPOCH_ISO].sort().pop();
    const to = endKey < yesterdayKey ? endKey : yesterdayKey;
    for (const k of dayKeysBetween(from, to)) {
      if (dayKeys.has(k)) continue;
      const info = infoFor(k);
      if (info && info.expected > 0) dayKeys.add(k);
    }
  }

  const rows = [];
  for (const date of dayKeys) {
    const events = (byDay.get(date) || []).slice()
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const work = computeDayWork(events, { timeZone: tz });
    const info = infoFor(date);
    const isToday = date === todayKey;
    const isFuture = date > todayKey;
    const isPast = date < todayKey;
    const liveWork = events.filter((e) => isWorkTimeEvent(e) && !isRejectedWorkTimeEvent(e));
    const warningTypes = work.warnings.map((w) => w.type);

    // Días anteriores al alta: ni jornada esperada ni diferencia (la bolsa no
    // los cuenta, así que la tabla tampoco puede sumarlos).
    const outOfScope = !!info && !!info.beforeStart;
    const expectedMinutes = info && !outOfScope ? info.expected : null;
    const balanceMinutes = info && isPast && !outOfScope ? work.workedMinutes - info.expected : null;
    // Horas compensadas aprobadas ese día (compensación por tramos). El día
    // está justificado: ni "Sin fichar" ni "No llega" ni incidencia.
    const partialCompensation = !!(info && info.partialCompensation);

    const flags = {
      sinCerrar: isPast && (work.openClockIn || work.openPause),
      sinAbrir: warningTypes.some((t) => SIN_ABRIR_WARNINGS.has(t)),
      sinFichar: !!info && !outOfScope && !partialCompensation && isPast && info.expected > 0 && liveWork.length === 0 && date >= BALANCE_EPOCH_ISO,
      porAprobar: events.some(isPendingApproval),
    };
    const otherWarnings = Array.from(new Set(warningTypes.filter(
      (t) => !SIN_ABRIR_WARNINGS.has(t) && !SIN_CERRAR_WARNINGS.has(t),
    )));
    const pendiente = flags.sinCerrar || flags.sinAbrir || flags.sinFichar || flags.porAprobar;
    const weekday = dowOfDayKey(date);

    rows.push({
      date,
      weekday,
      weekdayLabel: WEEKDAY_LABELS[weekday] || '',
      events,
      firstIn: work.firstIn,
      lastOut: work.lastOut,
      clockIns: events.filter((e) => Number(e.eventType) === EVENT_TYPES.CLOCK_IN).length,
      clockOuts: events.filter((e) => Number(e.eventType) === EVENT_TYPES.CLOCK_OUT).length,
      workedMinutes: work.workedMinutes,
      pauseMinutes: work.pauseMinutes,
      expectedMinutes,
      balanceMinutes,
      // Minutos del slot con ausencia por horas aprobada. Solo informativo:
      // se recuperan, la jornada esperada NO se reduce.
      hourlyAbsenceMinutes: info ? info.hourlyAbsenceMinutes : 0,
      // Compensación por tramos: minutos del slot marcados como compensados.
      // Solo informativo — ni reducen la jornada esperada ni cuentan como
      // trabajados; el déficit del día es lo que se gasta de la bolsa.
      partialCompensation,
      compensationMinutes: info ? info.compensationMinutes : 0,
      dayType: info ? dayTypeFor(info) : (holidaysSet && typeof holidaysSet.has === 'function' && holidaysSet.has(date) ? 'festivo' : null),
      isToday,
      isFuture,
      enCurso: isToday && (work.openClockIn || work.openPause),
      inBalancePeriod: inPeriod(date),
      work,
      flags,
      otherWarnings,
      warningTypes,
      pendiente,
      conIncidencias: pendiente || otherWarnings.length > 0,
    });
  }
  rows.sort((a, b) => b.date.localeCompare(a.date));
  return { rows, noSchedule, balancePeriod, todayKey };
};

// ── Orden ─────────────────────────────────────────────────────────────────
// key: 'date' | 'worked' | 'balance'; dir: 'asc' | 'desc'. Los valores null
// (sin horario, hoy) van SIEMPRE al final. Empate → fecha descendente.
export const SORT_KEYS = ['date', 'worked', 'balance'];
export const sortHistoryDays = (rows, { key = 'date', dir = 'desc' } = {}) => {
  const sign = dir === 'asc' ? 1 : -1;
  const valueOf = (r) => {
    if (key === 'worked') return r.workedMinutes;
    if (key === 'balance') return r.balanceMinutes;
    return null;
  };
  return (rows || []).slice().sort((a, b) => {
    if (key === 'date') return sign * a.date.localeCompare(b.date);
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va == null && vb == null) return b.date.localeCompare(a.date);
    if (va == null) return 1;
    if (vb == null) return -1;
    if (va !== vb) return sign * (va - vb);
    return b.date.localeCompare(a.date);
  });
};

// ── Filtros ───────────────────────────────────────────────────────────────
const filterDef = (key) => HISTORY_STATUS_FILTERS.find((f) => f.key === key) || HISTORY_STATUS_FILTERS[0];
// opts.threshold = umbral de desviación en minutos (filtros exceso/defecto).
export const filterHistoryDays = (rows, key = 'todos', opts = {}) => {
  const { test } = filterDef(key);
  return (rows || []).filter((r) => test(r, opts));
};
export const countHistoryFilters = (rows, opts = {}) => {
  const out = {};
  for (const f of HISTORY_STATUS_FILTERS) out[f.key] = 0;
  for (const r of rows || []) {
    for (const f of HISTORY_STATUS_FILTERS) if (f.test(r, opts)) out[f.key] += 1;
  }
  return out;
};

// ── Resumen ───────────────────────────────────────────────────────────────
// Suma de las filas dadas. expected/balance solo cuentan filas con valor.
// exceso/defecto = nº de días que se pasan / no llegan con opts.threshold.
export const summarizeHistoryDays = (rows, opts = {}) => {
  let worked = 0;
  let expected = 0;
  let balance = 0;
  let hasExpected = false;
  let exceso = 0;
  let defecto = 0;
  for (const r of rows || []) {
    worked += r.workedMinutes || 0;
    if (r.expectedMinutes != null && !r.isToday && !r.isFuture) { expected += r.expectedMinutes; hasExpected = true; }
    if (r.balanceMinutes != null) balance += r.balanceMinutes;
    const dev = classifyDeviation(r, opts.threshold);
    if (dev === 'exceso') exceso += 1;
    else if (dev === 'defecto') defecto += 1;
  }
  return {
    days: (rows || []).length,
    worked,
    expected: hasExpected ? expected : null,
    balance: hasExpected ? balance : null,
    exceso,
    defecto,
  };
};

// ── Conciliación con la bolsa ─────────────────────────────────────────────
// `bolsa` = { startKey, endKey, totalMinutes, byDay } (periodo de
// balanceComputationRange + resultado de computeAutoBalance). Compara, en el
// tramo del periodo cubierto por el rango del historial [rangeStart, rangeEnd]:
//   · bolsaMinutes = suma de los delta de la bolsa en ese tramo (si el tramo
//                    es el periodo entero, es el total automático);
//   · tableMinutes = suma de balanceMinutes de TODAS las filas del tramo (sin
//                    filtros de estado ni de tipo).
// Devuelve null si no hay periodo o no se solapa con el rango.
export const reconcileWithBalance = (rows, bolsa, rangeStart, rangeEnd) => {
  if (!bolsa || !bolsa.startKey || !bolsa.endKey || bolsa.startKey > bolsa.endKey) return null;
  const coverStart = rangeStart && rangeStart > bolsa.startKey ? rangeStart : bolsa.startKey;
  const coverEnd = rangeEnd && rangeEnd < bolsa.endKey ? rangeEnd : bolsa.endKey;
  const fullTotal = Number(bolsa.totalMinutes) || 0;
  if (coverStart > coverEnd) {
    return { startKey: bolsa.startKey, endKey: bolsa.endKey, fullTotal, covers: false, overlap: false };
  }
  const covers = coverStart === bolsa.startKey && coverEnd === bolsa.endKey;
  const bolsaMinutes = covers
    ? fullTotal
    : (bolsa.byDay || []).filter((d) => d.date >= coverStart && d.date <= coverEnd).reduce((s, d) => s + d.delta, 0);
  const tableMinutes = (rows || [])
    .filter((r) => r.date >= coverStart && r.date <= coverEnd && r.balanceMinutes != null)
    .reduce((s, r) => s + r.balanceMinutes, 0);
  return {
    startKey: bolsa.startKey,
    endKey: bolsa.endKey,
    fullTotal,
    covers,
    overlap: true,
    coverStart,
    coverEnd,
    bolsaMinutes,
    tableMinutes,
    diff: tableMinutes - bolsaMinutes,
    ok: tableMinutes === bolsaMinutes,
  };
};

// ── Formato ───────────────────────────────────────────────────────────────
// h:mm con signo (−1:30 / +0:45 / 0:00). null → '—'.
export const formatSignedHm = (mins) => {
  if (mins == null || !Number.isFinite(Number(mins))) return '—';
  const m = Math.round(Number(mins));
  const abs = Math.abs(m);
  const body = `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
  if (m === 0) return `0:00`;
  return `${m > 0 ? '+' : '−'}${body}`;
};
export const formatHm = (mins) => {
  if (mins == null || !Number.isFinite(Number(mins))) return '—';
  const abs = Math.abs(Math.round(Number(mins)));
  return `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
};
